import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AppSidebar, { MobileTopBar, NavItem, hasIntensiveAccess } from "@/components/AppSidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, Users, ChevronRight, CalendarDays, BookMarked, LayoutGrid, Moon, ShieldCheck,
  Heart, MessageCircle, Sparkles, CheckCircle2, ArrowRight
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "react-hot-toast";

const apps = [
  {
    id: "community",
    name: "Community",
    description: "Share prayer requests, praise reports, Bible verses, and encouragement with everyone.",
    icon: Users,
    href: "/community",
    active: true,
    iconBg: "bg-teal-50",
    iconColor: "text-teal-600",
    accentColor: "text-teal-600",
    hoverBorder: "hover:border-teal-300",
  },
  {
    id: "events",
    name: "Events",
    description: "View upcoming church services, gatherings, and special events.",
    icon: CalendarDays,
    href: "/events",
    active: true,
    superAdminOnly: true,
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    accentColor: "text-amber-600",
    hoverBorder: "hover:border-amber-300",
  },
  {
    id: "discipleship",
    name: "Discipleship Intensive",
    description: "Manage assignments, journals, meetings, and track discipleship progress.",
    icon: BookOpen,
    href: null, // resolved dynamically based on role
    active: true,
    iconBg: "bg-slate-50",
    iconColor: "text-slate-600",
    accentColor: "text-slate-600",
    hoverBorder: "hover:border-slate-300",
  },
  {
    id: "guide",
    name: "Weekly Guide",
    description: "Follow the weekly discipleship guide — daily devotionals, sermon notes, reflection questions, and prayer.",
    icon: BookMarked,
    href: "/guide",
    active: true,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    accentColor: "text-emerald-600",
    hoverBorder: "hover:border-emerald-300",
  },
  {
    id: "wednesday",
    name: "Wednesday Nights",
    description: "Track our journey through Grudem's Systematic Theology — teaching videos, notes, and discussion questions.",
    icon: Moon,
    href: "/wednesday",
    active: true,
    iconBg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    accentColor: "text-indigo-600",
    hoverBorder: "hover:border-indigo-300",
  },
  {
    id: "admin",
    name: "Admin",
    description: "Manage users and control who has access to the Discipleship Intensive class.",
    icon: ShieldCheck,
    href: "/admin",
    active: true,
    superAdminOnly: true,
    iconBg: "bg-rose-50",
    iconColor: "text-rose-600",
    accentColor: "text-rose-600",
    hoverBorder: "hover:border-rose-300",
  },
];

const getInitials = (name) => name?.split(" ").map((n) => n[0]).join("").toUpperCase() || "?";

const CATEGORY_STYLES = {
  prayer: "bg-sky-50 text-sky-700 border-sky-200",
  praise: "bg-amber-50 text-amber-700 border-amber-200",
  announcement: "bg-purple-50 text-purple-700 border-purple-200",
  verse: "bg-teal-50 text-teal-700 border-teal-200",
};

const CommunityActivityWidget = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/feed/activity-summary?limit=5`)
      .then((res) => setSummary(res.data))
      .catch(() => toast.error("Failed to load community activity"))
      .finally(() => setLoading(false));
  }, []);

  const openPost = (postId) => {
    navigate("/community", { state: { openPostId: postId } });
  };

  return (
    <div data-testid="community-activity-widget" className="rounded-2xl border-2 border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-teal-600" />
          </div>
          <h2 className="font-semibold text-slate-900 text-base">Community Activity</h2>
        </div>
        <button
          data-testid="view-community-link"
          onClick={() => navigate("/community")}
          className="text-xs font-semibold text-teal-600 flex items-center gap-1 hover:text-teal-700"
        >
          View
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-6 h-6 border-2 border-slate-200 border-t-teal-500 rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Stats summary */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div data-testid="stat-posts-today" className="rounded-xl bg-teal-50/70 p-3">
              <p className="text-2xl font-bold text-teal-700">{summary?.posts_today ?? 0}</p>
              <p className="text-xs text-teal-700/80 mt-0.5">New posts today</p>
            </div>
            <div data-testid="stat-comments-today" className="rounded-xl bg-slate-50 p-3">
              <p className="text-2xl font-bold text-slate-700">{summary?.comments_today ?? 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">New comments today</p>
            </div>
          </div>

          {/* Recent posts */}
          {!summary?.recent_posts || summary.recent_posts.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No posts yet — be the first to share!</p>
          ) : (
            <div className="space-y-1">
              {summary.recent_posts.map((post) => (
                <div
                  key={post.post_id}
                  data-testid={`activity-post-${post.post_id}`}
                  onClick={() => openPost(post.post_id)}
                  className="flex items-start gap-3 py-3 border-t border-slate-100 first:border-t-0 cursor-pointer hover:bg-slate-50 rounded-lg -mx-2 px-2 transition-colors"
                >
                  <Avatar className="w-8 h-8 shrink-0">
                    <AvatarImage src={post.author_picture} />
                    <AvatarFallback className="bg-teal-100 text-teal-700 text-xs">
                      {getInitials(post.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-900">{post.author_name}</span>
                      {post.category && (
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${CATEGORY_STYLES[post.category] || ""}`}>
                          {post.category}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mt-0.5">{post.content}</p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" /> {post.like_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> {post.reply_count}
                      </span>
                      <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const UpNextWidget = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [guide, setGuide] = useState(null);
  const [guideCompletion, setGuideCompletion] = useState(null);
  const [session, setSession] = useState(null);
  const [sessionSubmitted, setSessionSubmitted] = useState(false);

  useEffect(() => {
    const loadGuide = async () => {
      try {
        const { data } = await axios.get(`${API}/guides/current`);
        setGuide(data);
        try {
          const { data: summary } = await axios.get(`${API}/guides/completion-summary`);
          setGuideCompletion(summary[data.guide_id] || null);
        } catch {
          // completion summary is a nice-to-have, ignore failures
        }
      } catch {
        setGuide(null);
      }
    };

    const loadSession = async () => {
      try {
        const { data } = await axios.get(`${API}/wednesday/current`);
        setSession(data);
        try {
          const { data: responses } = await axios.get(`${API}/wednesday/${data.session_id}/responses`);
          setSessionSubmitted(responses["discussion_submitted"]?.text === "true");
        } catch {
          // ignore, treat as not submitted
        }
      } catch {
        setSession(null);
      }
    };

    Promise.all([loadGuide(), loadSession()]).finally(() => setLoading(false));
  }, []);

  const noContent = !guide && !session;

  return (
    <div data-testid="up-next-widget" className="rounded-2xl border-2 border-slate-200 bg-white p-6">
      <h2 className="font-semibold text-slate-900 text-base mb-5">Up Next</h2>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="w-6 h-6 border-2 border-slate-200 border-t-emerald-500 rounded-full animate-spin" />
        </div>
      ) : noContent ? (
        <p className="text-sm text-slate-400 text-center py-6">Nothing published yet — check back soon!</p>
      ) : (
        <div className="space-y-4">
          {/* Weekly Guide */}
          {guide && (
            <div data-testid="up-next-guide" className="rounded-xl bg-emerald-50/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <BookMarked className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Weekly Guide</span>
              </div>
              <p className="text-sm font-medium text-slate-900">{guide.week_label || "This Week's Guide"}</p>
              {guide.theme && <p className="text-xs text-slate-500 mt-0.5">{guide.theme}</p>}
              <div className="flex items-center justify-between mt-3">
                {guideCompletion ? (
                  guideCompletion.all_done ? (
                    <span className="text-xs font-medium text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> All done this week
                    </span>
                  ) : (
                    <span className="text-xs text-slate-500">
                      {guideCompletion.completed}/{guideCompletion.applicable} days completed
                    </span>
                  )
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
                <button
                  data-testid="up-next-guide-continue"
                  onClick={() => navigate("/guide")}
                  className="text-xs font-semibold text-emerald-700 flex items-center gap-1 hover:text-emerald-800"
                >
                  Continue <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Wednesday Nights */}
          {session && (
            <div data-testid="up-next-wednesday" className="rounded-xl bg-indigo-50/60 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Moon className="w-3.5 h-3.5 text-indigo-600" />
                </div>
                <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Wednesday Nights</span>
              </div>
              <p className="text-sm font-medium text-slate-900">
                {session.chapter_number ? `Chapter ${session.chapter_number} — ` : ""}{session.topic || `Week ${session.week_number}`}
              </p>
              <div className="flex items-center justify-between mt-3">
                {sessionSubmitted ? (
                  <span className="text-xs font-medium text-indigo-700 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Discussion submitted
                  </span>
                ) : (
                  <span className="text-xs text-slate-500">Discussion questions pending</span>
                )}
                <button
                  data-testid="up-next-wednesday-continue"
                  onClick={() => navigate("/wednesday")}
                  className="text-xs font-semibold text-indigo-700 flex items-center gap-1 hover:text-indigo-800"
                >
                  Continue <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default function Home({ user }) {
  const navigate = useNavigate();

  const getAppHref = (app) => {
    if (app.id === "discipleship") {
      return user?.role === "teacher" ? "/teacher" : "/student";
    }
    return app.href;
  };

  const visibleApps = apps.filter((app) => {
    if (app.superAdminOnly) return Boolean(user?.is_super_admin);
    if (app.id === "discipleship") return hasIntensiveAccess(user);
    return true;
  });

  const navItems = [
    { icon: LayoutGrid, label: "Apps", active: true, onClick: () => {} },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <AppSidebar user={user} active="home" brandLabel="Home" navItems={navItems} />
      <MobileTopBar brandLabel="Home" />

      <div className="md:ml-[68px] xl:ml-[260px]">
        <main className="max-w-6xl mx-auto w-full px-6 py-14 pt-24 md:pt-14">
          <div className="mb-10">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-widest mb-2">
              Live Church Platform
            </p>
            <h1 className="font-serif text-4xl font-bold text-slate-900 mb-2">
              Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
            </h1>
            <p className="text-slate-500 text-base">
              Where would you like to go?
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {visibleApps.map((app) => {
                const Icon = app.icon;
                const href = getAppHref(app);
                return (
                  <div
                    key={app.id}
                    data-testid={`app-card-${app.id}`}
                    onClick={() => navigate(href)}
                    className={`
                      group relative rounded-2xl border-2 border-slate-200 bg-white p-6
                      transition-all duration-200 cursor-pointer hover:shadow-md ${app.hoverBorder}
                    `}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${app.iconBg}`}>
                        <Icon className={`w-5 h-5 ${app.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="font-semibold text-slate-900 text-base mb-1">
                          {app.name}
                        </h2>
                        <p className="text-sm text-slate-500 leading-relaxed">
                          {app.description}
                        </p>
                      </div>
                    </div>
                    <div className="mt-5 flex items-center justify-end">
                      <span className={`text-xs font-semibold flex items-center gap-1 transition-colors ${app.accentColor}`}>
                        Open
                        <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:col-span-1 space-y-6">
              <UpNextWidget />
              <CommunityActivityWidget />
            </div>
          </div>
        </main>

        <footer className="border-t border-slate-200 py-5 text-center">
          <p className="text-xs text-slate-400">
            Live Church &mdash; Growing disciples, one lesson at a time.
          </p>
        </footer>
      </div>
    </div>
  );
}
